/**
 * ORB(主+子) Top100 — master accounts ranked by family ORB total.
 * GET /api/admin/dashboard/top-orb-accounts
 */
import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Col, Row, Space, Statistic, Table, Tag, Typography } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../../api/client';

interface TopOrbRow {
  rank: number;
  id: number;
  account: string;
  player_number: string;
  freeze: 'frozen' | 'active';
  uo_main: string;
  uo_total: string;
  sub_count: number;
}

interface TopOrbResponse {
  rows: TopOrbRow[];
  total_masters: number;
  sum_uo_total: string;
  real_active_uo_total: string;
  frozen_rows: TopOrbRow[];
  frozen_uo_total: string;
}

function trimDecimalZeros(s: string): string {
  return s.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
}

function fmtWallet(v: string | undefined): string {
  if (!v || v === '0') return '0';
  const n = Number(v);
  if (!Number.isFinite(n)) return v;
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e8) return `${sign}${trimDecimalZeros((abs / 1e8).toFixed(2))}亿`;
  if (abs >= 1e7) return `${sign}${trimDecimalZeros((abs / 1e7).toFixed(2))}千万`;
  if (abs >= 1e6) return `${sign}${trimDecimalZeros((abs / 1e6).toFixed(2))}百万`;
  if (abs >= 1e4) return `${sign}${trimDecimalZeros((abs / 1e4).toFixed(2))}万`;
  return n.toLocaleString('zh-CN', { maximumFractionDigits: 0 });
}

function fmtNum(v: string): string {
  const n = Number(v);
  return Number.isFinite(n) ? n.toLocaleString('zh-CN', { maximumFractionDigits: 0 }) : v;
}

function phpUserLink(accountOrId: string | number): string {
  return `/parity/user_user?kind=master&q=${encodeURIComponent(String(accountOrId))}`;
}

const orbColumns = [
  { title: '排名', dataIndex: 'rank', width: 64, fixed: 'left' as const, align: 'center' as const },
  {
    title: '玩家编号',
    dataIndex: 'player_number',
    width: 100,
    render: (v: string) => v || '—',
  },
  {
    title: '账号',
    dataIndex: 'account',
    width: 140,
    ellipsis: true,
    render: (v: string, row: TopOrbRow) => <Link to={phpUserLink(v || row.id)}>{v}</Link>,
  },
  {
    title: '状态',
    dataIndex: 'freeze',
    width: 72,
    align: 'center' as const,
    render: (v: TopOrbRow['freeze']) => (
      <Tag color={v === 'frozen' ? 'red' : 'green'}>{v === 'frozen' ? '冻结' : '正常'}</Tag>
    ),
  },
  {
    title: 'ORB(主)',
    dataIndex: 'uo_main',
    width: 110,
    align: 'right' as const,
    render: (v: string) => fmtNum(v),
  },
  {
    title: 'ORB(主+子)',
    dataIndex: 'uo_total',
    width: 120,
    align: 'right' as const,
    render: (v: string) => <strong>{fmtNum(v)}</strong>,
  },
  { title: '子账户', dataIndex: 'sub_count', width: 72, align: 'right' as const },
];

export function OrbTop100Page() {
  const { data, isLoading, isError, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey: ['admin', 'dashboard', 'top-orb-accounts'],
    queryFn: () => api.get<TopOrbResponse>('/api/admin/dashboard/top-orb-accounts', { limit: 100 }),
    staleTime: 60_000,
  });

  const cardLoading = isLoading || (isFetching && !data);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>ORB(主+子) Top100</h2>
        <Space>
          {dataUpdatedAt > 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              更新于 {new Date(dataUpdatedAt).toLocaleTimeString()}
            </Typography.Text>
          )}
          <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => void refetch()}>
            刷新
          </Button>
        </Space>
      </div>

      {isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="加载失败"
          description={error instanceof Error ? error.message : String(error)}
        />
      )}

      <Typography.Paragraph type="secondary" style={{ marginTop: 0, marginBottom: 16, fontSize: 12 }}>
        与前端钱包 ORB(主+子) 同算法：主号 + 子账户 Σ floor(raw_uo)，扣 AP 捐币池。全平台合计含虚拟用户（与数据看板一致）；下方榜单不含虚拟用户。
      </Typography.Paragraph>

      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" loading={cardLoading}>
            <Statistic title="主号总数" value={data?.total_masters ?? '—'} />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" loading={cardLoading}>
            <Statistic
              title="全平台 ORB(主+子) 合计"
              value={fmtWallet(data?.sum_uo_total)}
              valueStyle={{ color: '#8c8c8c' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" loading={cardLoading}>
            <Statistic
              title="真实总额（扣虚拟·扣冻结）"
              value={fmtWallet(data?.real_active_uo_total)}
              valueStyle={{ color: '#3f8600' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card size="small" loading={cardLoading}>
            <Statistic
              title="冻结账户 ORB(主+子) 合计"
              value={fmtWallet(data?.frozen_uo_total)}
              valueStyle={{ color: '#cf1322' }}
            />
          </Card>
        </Col>
      </Row>

      <Typography.Title level={5} style={{ marginTop: 8 }}>
        正常榜单 · ORB(主+子) 大→小（不含虚拟用户）
      </Typography.Title>
      <Card size="small" loading={cardLoading} styles={{ body: { padding: 0 } }}>
        <Table<TopOrbRow>
          size="small"
          bordered
          rowKey="id"
          pagination={false}
          scroll={{ x: 720 }}
          dataSource={data?.rows ?? []}
          columns={orbColumns}
        />
      </Card>

      <Typography.Title level={5} style={{ marginTop: 24 }}>
        冻结账户 · ORB(主+子) 大→小（{data?.frozen_rows.length ?? 0} 户）
      </Typography.Title>
      <Card size="small" loading={cardLoading} styles={{ body: { padding: 0 } }}>
        <Table<TopOrbRow>
          size="small"
          bordered
          rowKey="id"
          pagination={false}
          scroll={{ x: 720 }}
          dataSource={data?.frozen_rows ?? []}
          columns={orbColumns}
          locale={{ emptyText: '暂无冻结账户' }}
        />
      </Card>
    </div>
  );
}

export default OrbTop100Page;
